import { Link, useParams } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import { websiteHref } from '@/features/common/organizationProfile';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import {
  institutionBlocks,
  institutionChips,
  institutionContactLine,
} from '@/features/publicsite/catalog/trainingInstitutionProfile';
import type { TrainingInstitution } from '@/features/publicsite/types/training/TrainingInstitution';
import { PUBLIC_RECORD } from '@/features/publicsite/publicRecord';
import type { PublicRecordState } from '@/features/publicsite/types/PublicRecordState';
import { usePublicRecord } from '@/features/publicsite/usePublicList';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { publicCatalog, type PublicCatalogKey } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

/** What each refusal reads as. A map, so a new state cannot quietly reuse the wrong sentence. */
const REFUSAL: Partial<Record<PublicRecordState, PublicCatalogKey>> = {
  MISSING: 'institutionNotFound',
  UNAVAILABLE: 'institutionUnavailable',
};

/**
 * /instruiri/institutii/:id — one institution's public profile (§6.5).
 *
 * Read from its own endpoint rather than by finding it in the list, unlike the research
 * organisation: the portfolio comes only from here, and an institution that is not PUBLISHED
 * answers 404 exactly as one that does not exist — which is the point, since the two must not be
 * distinguishable or the endpoint would say who has applied.
 */
export function TrainingInstitutionProfilePage() {
  const { t, language } = useTranslation(publicCatalog);
  const { id } = useParams<{ id: string }>();
  // Four states, not two: a fault must not be reported as "this institution does not exist".
  // Every text below is rendered as text, never as markup — these are fields an institution
  // typed about itself.
  const { state, item: institution } = usePublicRecord<TrainingInstitution>(
    publicCatalogApi.trainingInstitutionUrl(id ?? ''),
  );

  // Before the early returns, so the hook order never changes; null while the answer is out.
  usePublicPageTitle(
    state === PUBLIC_RECORD.loading
      ? null
      : (institution?.name ?? t(REFUSAL[state] ?? 'institutionNotFound')),
  );

  const back = (
    <Link className="head-link" to="/instruiri/institutii">
      {t('institutionBack')}
    </Link>
  );

  if (state === PUBLIC_RECORD.loading) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }

  if (!institution) {
    return (
      <section className="container section">
        <span className="eyebrow">{t('eyebrowCatalog')}</span>
        <p className="empty-state">{t(REFUSAL[state] ?? 'institutionNotFound')}</p>
        <p>{back}</p>
      </section>
    );
  }

  const blocks = institutionBlocks(institution);
  const contactLine = institutionContactLine(institution);
  const chips = institutionChips(institution, language);

  return (
    <section className="container section">
      <div className="section-head">
        <div>
          <span className="eyebrow">{t('eyebrowCatalog')}</span>
          <h1>{institution.name}</h1>
          <p className="muted">
            {t('institutionPrograms', { count: institution.programCount ?? 0 })} ·{' '}
            {t('institutionUpcoming', { count: institution.upcomingSessionCount ?? 0 })}
          </p>
        </div>
        {back}
      </div>

      {blocks.domains && (
        <div className="org-profile__field">
          <h2>{t('institutionDomainsHeading')}</h2>
          <ul
            className="chip-row advisor-card__domains"
            aria-label={t('institutionDomainsHeading')}
          >
            {chips.map((chip) => (
              <li key={chip.id} className="chip">
                {chip.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {blocks.overview && (
        <div className="org-profile__field">
          <h2>{t('orgOverviewHeading')}</h2>
          <p>{institution.overview}</p>
        </div>
      )}

      {blocks.teachingStaff && (
        <div className="org-profile__field">
          <h2>{t('institutionStaffHeading')}</h2>
          <p>{institution.teachingStaff}</p>
        </div>
      )}

      {blocks.infrastructure && (
        <div className="org-profile__field">
          <h2>{t('institutionInfrastructureHeading')}</h2>
          <p>{institution.infrastructure}</p>
        </div>
      )}

      {blocks.contact && (
        <div className="org-profile__field">
          <h2>{t('orgContactHeading')}</h2>
          <p>
            {contactLine}
            {institution.website && (
              <>
                {contactLine && ' · '}
                {websiteHref(institution.website) ? (
                  <a href={websiteHref(institution.website)!} target="_blank" rel="noreferrer">
                    {institution.website}
                  </a>
                ) : (
                  institution.website
                )}
              </>
            )}
          </p>
        </div>
      )}

      {blocks.zones && (
        <div className="org-profile__field">
          <h2>{t('institutionZonesHeading')}</h2>
          <p>{(institution.zoneNames ?? []).join(', ')}</p>
          {blocks.map && (
            <p>
              <Link className="btn btn--ghost" to={`/harta?subjectId=${institution.id}`}>
                <Icon name="map-pin" className="btn__icon" />
                {t('institutionSeeOnMap')}
              </Link>
            </p>
          )}
        </div>
      )}

      {blocks.programs && (
        <>
          <div className="section-head">
            <div>
              <h2>{t('institutionProgramsHeading')}</h2>
            </div>
          </div>
          <div className="card-grid">
            {(institution.programs ?? []).map((program) => (
              <CatalogCard
                key={program.id}
                id={`program-${program.id}`}
                badge={t(program.online ? 'trainingsOnline' : 'trainingsOnSite')}
                title={program.title}
                subtitle={
                  (program.domainLabels ?? []).length > 0
                    ? institutionChips(
                        { specializationDomainLabels: program.domainLabels },
                        language,
                      )
                        .map((chip) => chip.label)
                        .join(', ')
                    : null
                }
              >
                {program.description && <p>{program.description}</p>}
              </CatalogCard>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
